import {
  getAuthToken as cognitoGetAuthToken,
  isUserAuthenticated as cognitoIsAuthenticated,
  autoLogin as cognitoAutoLogin,
  onAuthStateChanged as cognitoOnAuthStateChanged,
} from './cognito';
import { sanitizeErrorMessage } from './utils/sanitizeError';

/** API Gateway stage root (no trailing slash). Set via REACT_APP_LAMBDA_BASE_URL in .env.development / .env.production */
export const lambda =
  process.env.REACT_APP_LAMBDA_BASE_URL ||
  'https://pipj8zbwne.execute-api.eu-central-1.amazonaws.com/prod';

/** User-Agent sent with all requests to the API (helps avoid 403 from CloudFront/WAF). */
export const API_USER_AGENT = typeof navigator !== 'undefined' ? navigator.userAgent : 'Giftbox-Web/1.0';

/** Subscribe page – Mobibox POSDK (extra params from /subscribe must use & when appended) */
export const SUBSCRIBE_PAGE_URL = 'https://posdk.mobibox.io/1/?GCampaignID=b045b01b-5171-4c61-aa17-0c7b643bcddf';

/** Merge React Router search (e.g. "?id=1") onto SUBSCRIBE_PAGE_URL without a second "?". */
export function buildSubscribePageUrl(search = '') {
  const raw = (search || '').trim();
  if (!raw) return SUBSCRIBE_PAGE_URL;
  const params = raw.startsWith('?') ? raw.slice(1) : raw;
  if (!params) return SUBSCRIBE_PAGE_URL;
  const sep = SUBSCRIBE_PAGE_URL.includes('?') ? '&' : '?';
  return `${SUBSCRIBE_PAGE_URL}${sep}${params}`;
}


export const services = {
  1: {
    "id": "1",
    "name": "Mobile"
  },
  3: {
    "id": "3",
    "name": "Utilities"
  },
  4: {
    "id": "4",
    "name": "GiftCards"
  },
  5: {
    "id": "5",
    "name": "A2P Messaging"
  },
  7: {
    "id": "7",
    "name": "Insurance"
  },
  10: {
    "id": "10",
    "name": "Finance"
  },
  11: {
    "id": "11",
    "name": "Government"
  },
  12: {
    "id": "12",
    "name": "Education"
  },
  13: {
    "id": "13",
    "name": "eSIM"
  }
};


// SECRET_KEY should be in environment variables, not exposed in client code
// For now, using fallback but this should be moved server-side
export const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || "X,sod9@jnd93ri43_=-2@#";

export let products = {};

export async function isUserAuthenticated() {
  return cognitoIsAuthenticated();
}

export async function getAuthToken() {
  return cognitoGetAuthToken();
}

// Remove every key ending with _id (user_id, currency_id, status_id, etc.) from history so they are never sent/used.
// Keeps only top-level 'id' on each order and subscription (for display/keys). Nested objects get all *_id removed.
function stripIdFieldsDeep(obj, keepId) {
  if (obj == null) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map((item) => stripIdFieldsDeep(item, false));
  const out = {};
  for (const k of Object.keys(obj)) {
    if (k.endsWith('_id')) continue;
    if (k === 'id' && !keepId) continue;
    out[k] = stripIdFieldsDeep(obj[k], false);
  }
  return out;
}

function stripIdFieldsFromOrder(order) {
  if (!order || typeof order !== 'object') return order;
  return stripIdFieldsDeep(order, true);
}

function stripIdFieldsFromSubscription(sub) {
  if (!sub || typeof sub !== 'object') return sub;
  return stripIdFieldsDeep(sub, true);
}

// Get full history for the current user (orders with status + vouchers, and subscriptions).
// Backend: GET /giftbox/history (Bearer token). Resolve user_id from token, then call getHistoryForUser(userId) from dbGet.js; return { orders, subscriptions }.
// Response is sanitized: user_id, currency_id, status_id and other *_id fields are stripped from orders/subscriptions.
export async function getHistory() {
  try {
    const token = await getAuthToken();
    if (!token) return { success: false, orders: [], subscriptions: [] };
    const res = await fetch(`${lambda}/giftbox/history`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': API_USER_AGENT,
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, orders: [], subscriptions: [] };
    const orders = Array.isArray(data.orders) ? data.orders.map(stripIdFieldsFromOrder) : [];
    const subscriptions = Array.isArray(data.subscriptions) ? data.subscriptions.map(stripIdFieldsFromSubscription) : [];
    return { success: true, orders, subscriptions };
  } catch (e) {
    return { success: false, orders: [], subscriptions: [] };
  }
}

/**
 * Services + subservices for filter UI.
 * Optional query params (Lambda should scope counts like get-products):
 * - productTypes: string[] → `product_types=TYPE1,TYPE2`
 * - providerId: number → `provider_id=` (e.g. 2 for A2P / A2P Messaging catalog only)
 * - country: string → `country=` (PIN / RECHARGE counts for that country when set)
 * - operatorId: number → `operator_id=` (mobile data: counts for that operator’s catalog)
 */
export async function GetServices(options = {}) {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams();
    if (Array.isArray(options.productTypes) && options.productTypes.length > 0) {
      params.set('product_types', options.productTypes.filter(Boolean).join(','));
    }
    if (options.providerId != null && options.providerId !== '') {
      const n = Number(options.providerId);
      if (Number.isFinite(n)) params.set('provider_id', String(n));
    }
    if (options.country != null && String(options.country).trim()) {
      params.set('country', String(options.country).trim());
    }
    if (options.operatorId != null && options.operatorId !== '') {
      const oid = Number(options.operatorId);
      if (Number.isFinite(oid)) params.set('operator_id', String(oid));
    }
    const qs = params.toString();
    const url = qs ? `${lambda}/giftbox/services?${qs}` : `${lambda}/giftbox/services`;
    const headers = {
      'Content-Type': 'application/json',
      'User-Agent': API_USER_AGENT,
      ...(token && { Authorization: `Bearer ${token}` }),
    };
    const response = await fetch(url, { headers });
    const data = await response.json();
    const services = Array.isArray(data) ? data : (data.services || []);
    if (!Array.isArray(data) && data.success === false) {
      throw new Error(sanitizeErrorMessage(data.message) || 'Failed to fetch services');
    }
    return { success: true, services };
  } catch (error) {
    throw error;
  }
}

/**
 * Distinct country names for filter dropdown. Backend chooses catalog by provider_id:
 * - providerId 1 → products (PIN / RECHARGE types; optional serviceId scopes by service_id)
 * - providerId 2 → products where type = A2P
 */
export async function GetCountries(options = {}) {
  try {
    const token = await getAuthToken();
    const providerId =
      options.providerId != null && options.providerId !== ''
        ? Number(options.providerId)
        : 1;
    const params = new URLSearchParams();
    params.set('provider_id', String(Number.isFinite(providerId) ? providerId : 1));
    if (options.serviceId != null && options.serviceId !== '') {
      const sid = Number(options.serviceId);
      if (Number.isFinite(sid)) {
        params.set('service_id', String(sid));
      }
    }
    const url = `${lambda}/giftbox/products/countries?${params.toString()}`;
    const headers = {
      'Content-Type': 'application/json',
      'User-Agent': API_USER_AGENT,
      ...(token && { Authorization: `Bearer ${token}` }),
    };
    const response = await fetch(url, { headers });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { success: false, countries: [] };
    }
    const countries = Array.isArray(data.countries) ? data.countries : (data.data?.countries ?? []);
    return { success: true, countries };
  } catch (_error) {
    return { success: false, countries: [] };
  }
}

/** Union of distinct country names: DT catalog (scoped by serviceId, default gift service 1) + A2P. */
export async function GetCountriesMergedDtAndA2p(serviceIdForDt = 1) {
  const sid =
    serviceIdForDt != null && serviceIdForDt !== '' ? Number(serviceIdForDt) : 1;
  const [dt, a2p] = await Promise.all([
    GetCountries({ providerId: 1, serviceId: Number.isFinite(sid) ? sid : 1 }),
    GetCountries({ providerId: 2 }),
  ]);
  const set = new Set([
    ...(Array.isArray(dt.countries) ? dt.countries : []),
    ...(Array.isArray(a2p.countries) ? a2p.countries : []),
  ]);
  return {
    success: true,
    countries: Array.from(set).filter(Boolean).sort((a, b) => String(a).localeCompare(String(b))),
  };
}

// Mobile number lookup – returns operators for Carrier Topups (DT One).
export async function MobileNumberLookup(mobileNumber) {
  const trimmed = typeof mobileNumber === 'string' ? mobileNumber.trim() : '';
  if (!trimmed) throw new Error('Mobile number is required');
  const res = await fetch(`${lambda}/giftbox/lookup/mobile-number`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'User-Agent': API_USER_AGENT,
    },
    body: JSON.stringify({ mobile_number: trimmed }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Lookup failed');
  return Array.isArray(data) ? data : [];
}

// New GetProducts function - fetches from Lambda (paginated, optional operator_id, product_types, provider_id for A2P, sort_by)
export async function GetProducts(serviceId = null, perPage = 25, page = 1, search = null, subserviceId = null, country = null, operatorId = null, productTypes = null, providerId = null, sortBy = null) {
  try {
    const token = await getAuthToken();
    let url = `${lambda}/giftbox/products/get-products?per_page=${perPage}&page=${page}`;
    
    if (serviceId) {
      url += `&service_id=${serviceId}`;
    }
    if (providerId != null && providerId !== '') {
      url += `&provider_id=${encodeURIComponent(String(providerId))}`;
    }
    if (subserviceId != null && subserviceId !== '') {
      url += `&subservice_id=${subserviceId}`;
    }
    if (country != null && String(country).trim()) {
      url += `&country=${encodeURIComponent(String(country).trim())}`;
    }
    if (search != null && String(search).trim()) {
      url += `&search=${encodeURIComponent(String(search).trim())}`;
    }
    if (operatorId != null && operatorId !== '') {
      url += `&operator_id=${encodeURIComponent(String(operatorId))}`;
    }
    if (productTypes != null && Array.isArray(productTypes) && productTypes.length > 0) {
      url += `&product_types=${encodeURIComponent(JSON.stringify(productTypes))}`;
    }
    if (sortBy != null && String(sortBy).trim()) {
      url += `&sort_by=${encodeURIComponent(String(sortBy).trim())}`;
    }

    const headers = {
      'Content-Type': 'application/json',
      'User-Agent': API_USER_AGENT,
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(url, { headers });
    const text = await response.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseErr) {
      throw new Error('Invalid response from server');
    }

    if (!response.ok) {
      throw new Error(sanitizeErrorMessage(data.message || data.error) || `Request failed (${response.status})`);
    }

    if (!data.success) {
      throw new Error(sanitizeErrorMessage(data.message) || 'Failed to fetch products');
    }

    return { ...data, products: Array.isArray(data.products) ? data.products : [] };
  } catch (error) {
    throw error;
  }
}

const A2P_COUNTRY_SCAN_MAX_PAGES = 30;
const A2P_COUNTRY_SCAN_PAGE_SIZE = 100;

/** Distinct country_name from A2P catalog via get-products (provider_id=2 → type A2P). Fallback if /countries?provider_id=2 is unavailable. */
export async function getDistinctCountryNamesFromA2pCatalog() {
  const names = new Set();
  let page = 1;
  while (page <= A2P_COUNTRY_SCAN_MAX_PAGES) {
    const res = await GetProducts(
      null,
      A2P_COUNTRY_SCAN_PAGE_SIZE,
      page,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      2
    );
    if (!res.success || !Array.isArray(res.products) || res.products.length === 0) break;
    for (const p of res.products) {
      const c = p?.country_name;
      if (c != null && String(c).trim()) names.add(String(c).trim());
    }
    const total = res.totalProducts != null ? res.totalProducts : res.products.length;
    if (page * A2P_COUNTRY_SCAN_PAGE_SIZE >= total || res.products.length < A2P_COUNTRY_SCAN_PAGE_SIZE) {
      break;
    }
    page += 1;
  }
  return Array.from(names).sort((a, b) => String(a).localeCompare(String(b)));
}

// GetProductById function - fetches single product from Lambda
export async function GetProductById(serviceId, productId) {
  try {
    const token = await getAuthToken();
    let url = `${lambda}/giftbox/products/get-product-by-id?product_id=${productId}`;
    
    if (serviceId) {
      url += `&service_id=${serviceId}`;
    }

    const headers = {
      'Content-Type': 'application/json',
      'User-Agent': API_USER_AGENT,
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(url, { headers });
    const data = await response.json();

    if (!data.success) {
      throw new Error(sanitizeErrorMessage(data.message) || 'Failed to fetch product');
    }

    const product = data.product;
    const RANGED_TYPES = ['RANGED_VALUE_RECHARGE', 'RANGED_VALUE_PIN_PURCHASE', 'RANGED_VALUE_PAYMENT'];
    const raw = product.raw_response;
    const dest = raw?.destination?.amount;
    const destUnit = raw?.destination?.unit || '';
    const priceUSD = raw?.prices?.retail?.amount;
    const useRawRanged = RANGED_TYPES.includes(product.type || '') && dest && priceUSD != null &&
      typeof priceUSD.min === 'number' && typeof priceUSD.max === 'number';

    let retailAmount;
    let singleAmount = null;
    if (useRawRanged) {
      const destMin = Number(dest.min) || 0;
      const destMax = Number(dest.max) || 0;
      const destStep = Number(dest.increment ?? dest.step) || 1;
      retailAmount = {
        destinationUnit: destUnit,
        destinationMin: destMin,
        destinationMax: destMax,
        destinationStep: destStep,
        priceMinUSD: Number(priceUSD.min),
        priceMaxUSD: Number(priceUSD.max)
      };
    } else {
      const minA = Number(product.retail_min_amount) || 0;
      const maxA = Number(product.retail_max_amount) || 0;
      const stepA = Number(product.retail_step_amount) || 1;
      const isRanged = RANGED_TYPES.includes(product.type || '') && minA !== maxA;
      singleAmount = !isRanged ? (product.retail_amount != null ? Number(product.retail_amount) : maxA || minA) : null;
      retailAmount = isRanged ? { min: minA, max: maxA, step: stepA } : singleAmount;
    }

    const minA = Number(product.retail_min_amount) || 0;
    const maxA = Number(product.retail_max_amount) || 0;
    const wholesaleAmount = singleAmount != null ? singleAmount * 0.7 : (retailAmount?.priceMinUSD != null && retailAmount?.priceMaxUSD != null)
      ? (retailAmount.priceMinUSD + retailAmount.priceMaxUSD) / 2 * 0.7
      : (minA + maxA) / 2 * 0.7;

    let category = 'Gift Cards';
    const serviceName = product.service_name || '';
    if (serviceName.toLowerCase().includes('mobile')) category = 'Mobile';
    else if (serviceName.toLowerCase().includes('utilit')) category = 'Utilities';
    else if (serviceName.toLowerCase().includes('gift')) category = 'Gift Cards';
    else if (product.service_id === 5 || (product.subservice_name || '').toLowerCase() === 'bulk sms') category = 'A2P Messaging';

    const operator_name = product.raw_response?.operator?.name || product.operator_name || null;
    return {
      id: product.dtshop_id,
      name: product.name,
      description: product.description,
      image: product.image_url,
      country_name: product.country_name,
      service_id: product.service_id,
      operator_name,
      subservice_id: product.subservice_id ?? null,
      subservice_name: product.subservice_name || null,
      provider_id: product.provider_id != null ? Number(product.provider_id) : null,
      category,
      type: product.type || null,
      raw_response: product.raw_response || null,
      service: { id: product.service_id, name: product.service_name },
      prices: {
        retail: { amount: retailAmount, unit: product.retail_currency, unit_type: "CURRENCY" },
        wholesale: { amount: wholesaleAmount, unit: product.retail_currency, unit_type: "CURRENCY" }
      },
      validity: { quantity: product.validity_quantity, unit: product.validity_unit },
      pin_terms: product.pin_terms
    };
  } catch (error) {
    throw error;
  }
}

// Returns the path to navigate to, or null if invalid
// Components should use this with React Router's navigate
export function getCheckoutPath(product, amount) {
  // Validate product first
  if (!product || !product.service || !product.service.id || !product.id) {
    return null;
  }

  const session = sessionStorage.getItem("gbx_data");
  const uid = getCookie("gbx_id");

  if (session && uid) {
    if(amount){
      return `/checkout?sid=${product.service.id}&pid=${product.id}&productamount=${amount}`;
    }else{
      return `/checkout?sid=${product.service.id}&pid=${product.id}`;
    }
  } else {
    return `/signin?sid=${product.service.id}&pid=${product.id}`;
  }
}

// Legacy function for backward compatibility - use getCheckoutPath instead
export async function GoToCheckout(product, amount, navigate) {
  const path = getCheckoutPath(product, amount);
  if (path && navigate) {
    navigate(path);
  } else if (path) {
    // Fallback to window.location if navigate not provided
    window.location.href = path;
  }
}


// Set Cookie
export function setSecureCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000)); // Set expiration in days
  const secureFlag = window.location.protocol === "https:" ? "; Secure" : "";  // Add Secure flag for HTTPS
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Strict${secureFlag};`;
}

// Get Cookie
export function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
}



export const autoLogin = async () => {
  return cognitoAutoLogin();
};



export function removeCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict;`;
}
